import { WithListContext } from 'ra-core';
import { Link, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ListMagnifyingGlassIcon,
  ArchiveTrayIcon,
} from '@phosphor-icons/react';
import { Skeleton } from '@/components/ui/skeleton';
import { Offer } from '../types';
import { DISPLAY_TIME_ZONE } from '../../../constants';

const dateFormat = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: DISPLAY_TIME_ZONE,
});

function formatDate(value: string | undefined) {
  if (!value) return '—';
  return dateFormat.format(new Date(value));
}

const H = 'h-11 py-2 px-3 text-xs font-semibold text-foreground';
const H1 = `${H} pl-5`;
const C = 'h-[53px] py-2 px-3 text-sm font-medium text-muted-foreground';
const C1 = `${C} pl-5`;

function StatusPill({ status }: { status: string }) {
  const isEnded = status === 'ended';
  return (
    <span
      className={
        isEnded
          ? 'inline-flex items-center h-6 px-2 rounded-md text-xs font-medium bg-muted text-muted-foreground border border-border'
          : 'inline-flex items-center h-6 px-2 rounded-md text-xs font-medium bg-destructive/15 text-destructive border border-destructive/25'
      }
    >
      {isEnded ? 'Ended' : 'Declined'}
    </span>
  );
}

const SKELETON_COLS = [200, 120, 100, 80, 120, 120] as const;

function TableSkeleton() {
  return (
    <div className="rounded-xl overflow-hidden border border-border shadow-premium [&_thead]:bg-card [&_thead_tr]:border-b [&_tbody]:bg-secondary">
      <Table>
        <TableHeader>
          <TableRow className="border-b border-border">
            <TableHead className={H1}>Offer Name</TableHead>
            <TableHead className={H}>Brand</TableHead>
            <TableHead className={H}>P.O Number</TableHead>
            <TableHead className={H}>Status</TableHead>
            <TableHead className={H}>Start Date</TableHead>
            <TableHead className={H}>End Date</TableHead>
            <TableHead className={`${H} w-[151px]`} />
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: 5 }).map((_, i) => (
            <TableRow key={i}>
              {SKELETON_COLS.map((w, j) => (
                <TableCell key={j} className={j === 0 ? C1 : C}>
                  <Skeleton className={`h-4 w-[${w}px] max-w-full`} />
                </TableCell>
              ))}
              <TableCell className={C} />
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function EmptyFinished() {
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
      <div className="size-12 rounded-full bg-muted flex items-center justify-center">
        <ArchiveTrayIcon
          weight="duotone"
          className="size-6 text-muted-foreground"
        />
      </div>
      <div>
        <h4 className="text-base font-semibold text-foreground">
          No finished offers yet
        </h4>
        <p className="text-sm text-muted-foreground mt-1 max-w-sm">
          Once an offer has finished running, you'll be able to view it here.
        </p>
      </div>
    </div>
  );
}

export function FinishedOffersTable({
  searchQuery = '',
}: {
  searchQuery?: string;
}) {
  const { publisherId } = useParams<{ publisherId: string }>();

  return (
    <WithListContext<Offer>
      render={({ data: offers, isLoading, isFetching, total }) => {
        // Show skeleton on first load — guard on both isLoading and undefined
        // (data can be undefined for a frame before react-query populates it)
        if (isLoading || offers === undefined) {
          return <TableSkeleton />;
        }

        // Empty state — no finished offers at all
        if (total === 0) {
          return <EmptyFinished />;
        }

        const filtered = searchQuery.trim()
          ? offers.filter(o =>
              o.name.toLowerCase().includes(searchQuery.trim().toLowerCase())
            )
          : offers;

        return (
          <div className="relative">
            {isFetching && (
              <div className="absolute top-0 left-0 w-full h-0.5 bg-border rounded-full overflow-hidden z-10">
                <div className="h-full w-1/3 bg-foreground/25 rounded-full animate-shimmer" />
              </div>
            )}
            <div className="rounded-xl overflow-hidden border border-border shadow-premium [&_thead]:bg-card [&_thead_tr]:border-b [&_tbody]:bg-secondary">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-border">
                    <TableHead className={H1}>Offer Name</TableHead>
                    <TableHead className={H}>Brand</TableHead>
                    <TableHead className={H}>P.O Number</TableHead>
                    <TableHead className={H}>Status</TableHead>
                    <TableHead className={H}>Start Date</TableHead>
                    <TableHead className={H}>End Date</TableHead>
                    <TableHead className={`${H} w-[151px]`} />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="h-24 text-center text-sm text-muted-foreground"
                      >
                        No offers match your search.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filtered.map(offer => (
                      <TableRow
                        key={offer.id}
                        className="border-b border-border last:border-0"
                      >
                        <TableCell className={`${C1} w-[280px]`}>
                          <span className="text-foreground font-medium">
                            {offer.name}
                          </span>
                        </TableCell>
                        <TableCell className={C}>
                          {offer.brand?.name ?? '—'}
                        </TableCell>
                        <TableCell className={C}>
                          {offer.po_number ? (
                            <span className="inline-flex items-center h-6 px-2 bg-card border border-border rounded-sm text-xs font-medium text-muted-foreground font-tabular">
                              {offer.po_number}
                            </span>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell className={C}>
                          <StatusPill status={offer.status} />
                        </TableCell>
                        <TableCell className={`${C} font-tabular`}>
                          {formatDate(offer.start_date)}
                        </TableCell>
                        <TableCell className={`${C} font-tabular`}>
                          {formatDate(offer.end_date)}
                        </TableCell>
                        <TableCell className={`${C} w-[151px] text-right`}>
                          <Button
                            asChild
                            size="sm"
                            variant="secondary"
                            className="border border-border gap-1.5 rounded-md"
                          >
                            <Link
                              to={`/publisher/${publisherId}/offers/show/${offer.id}`}
                            >
                              <ListMagnifyingGlassIcon className="size-4" />
                              View Details
                            </Link>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        );
      }}
    />
  );
}
